import { useCallback } from "react";
import { medicalsApi } from "../../../../../../api/medicalsApi";
import { TCopilotNotePopulationStatus } from "../../../../../../types/copilot";
import {
  buildPopulationStatusUpdate,
  clearSectionsHumanEditedAfterApply,
  CopilotHumanEditedSection,
  getPopulationFieldAcceptTooltip,
  getSectionAcceptTooltip,
  getSectionsForPopulationField,
  isSectionHumanEdited,
} from "../components/utils/copilotExtensions";
import { useCopilotClinicalContext } from "../useCopilotClinicalContext";

type ApplySectionOptions = {
  sections: CopilotHumanEditedSection[];
  populationStatusField?: keyof TCopilotNotePopulationStatus;
  populate: () => Promise<unknown>;
};

export function useCopilotSectionApply() {
  const { copilotData, setCopilotData, encounterHeaders } =
    useCopilotClinicalContext();

  const clearAfterApply = useCallback(
    async (
      sections: CopilotHumanEditedSection[],
      populationStatusField?: keyof TCopilotNotePopulationStatus,
    ) => {
      if (!copilotData?.id) {
        return;
      }

      const sectionsToClear = sections.filter((section) =>
        isSectionHumanEdited(copilotData, section),
      );
      const populationStatus = populationStatusField
        ? buildPopulationStatusUpdate(populationStatusField)
        : undefined;

      if (!sectionsToClear.length && !populationStatus) {
        return;
      }

      const updatedCopilot = clearSectionsHumanEditedAfterApply(
        copilotData,
        sectionsToClear,
        { populationStatus },
      );

      const response = await medicalsApi.updateCopilotNote(
        copilotData.id,
        updatedCopilot,
        encounterHeaders,
      );
      setCopilotData(response.data);
    },
    [copilotData, encounterHeaders, setCopilotData],
  );

  const applySection = useCallback(
    async ({
      sections,
      populationStatusField,
      populate,
    }: ApplySectionOptions) => {
      await populate();
      await clearAfterApply(sections, populationStatusField);
    },
    [clearAfterApply],
  );

  const getAcceptTooltip = useCallback(
    (
      section: CopilotHumanEditedSection,
      defaultMessage = "Add content from the note to the chart",
    ) => getSectionAcceptTooltip(copilotData, section, defaultMessage),
    [copilotData],
  );

  const getPopulationAcceptTooltip = useCallback(
    (
      field: keyof TCopilotNotePopulationStatus,
      defaultMessage = "Add content from the note to the chart",
    ) => getPopulationFieldAcceptTooltip(copilotData, field, defaultMessage),
    [copilotData],
  );

  const getPopulationSections = useCallback(
    (field: keyof TCopilotNotePopulationStatus) =>
      getSectionsForPopulationField(field),
    [],
  );

  return {
    applySection,
    clearAfterApply,
    getAcceptTooltip,
    getPopulationAcceptTooltip,
    getPopulationSections,
  };
}
